import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2, Send, RefreshCw, AlertTriangle, Info } from "lucide-react";
import { smsInfo } from "../lib/sms";

export default function SendSmsDialog({
  open,
  onOpenChange,
  defaultTo = "",
  defaultMessage = "",
  onSend,
  sending,
  smsConfigured,
  isRetry = false,
}) {
  const [to, setTo] = useState(defaultTo);
  const [text, setText] = useState(defaultMessage);

  // Sync inputs with new defaults whenever the dialog opens (retry vs first send have different values)
  useEffect(() => {
    if (open) {
      setTo(defaultTo || "");
      setText(defaultMessage || "");
    }
  }, [open, defaultTo, defaultMessage]);

  const submit = async (e) => {
    e.preventDefault();
    await onSend({ to_number: to.trim(), message: text.trim() });
  };

  const disabled = sending || !smsConfigured;
  const title = isRetry ? "Try again" : "Send this message";
  const description = smsConfigured
    ? (isRetry
        ? "Confirm the recipient and the exact message before FINALLOOK retries the SMS. No new AI review will be run."
        : "Confirm the recipient and the message. FINALLOOK only sends after you tap Send.")
    : "SMS is not configured on the server yet. Add Twilio credentials to backend .env, then try again.";
  const info = smsInfo(text);
  const isMulti = info.segments > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="send-sms-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {isRetry && <RefreshCw className="h-4 w-4 text-primary" />}
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {isRetry && smsConfigured && (
          <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-foreground/90 px-3 py-2 flex items-start gap-2 text-xs" data-testid="send-sms-retry-notice">
            <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--warning))] shrink-0 mt-0.5" />
            <span>The previous attempt failed. Double-check the number and text — they are prefilled from your last approved send.</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sms-to">Recipient phone (international format)</Label>
            <Input
              id="sms-to"
              type="tel"
              inputMode="tel"
              required
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="+15551234567"
              className="h-11 font-mono"
              disabled={disabled}
              data-testid="send-sms-to-input"
            />
            <p className="text-xs text-muted-foreground">Use E.164 format: + then country code then number, no spaces.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sms-body">Message</Label>
            <Textarea
              id="sms-body"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={1600}
              rows={5}
              className="text-sm leading-relaxed"
              disabled={disabled}
              data-testid="send-sms-body-input"
            />
            {(() => {
              return (
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs" data-testid="send-sms-segments">
                  <span className={isMulti ? "text-[hsl(var(--warning))]" : "text-muted-foreground"}>
                    <span data-testid="send-sms-char-count">{info.chars}</span> characters
                    {" • "}
                    <span data-testid="send-sms-segment-count">{info.segments}</span> SMS {info.segments === 1 ? "segment" : "segments"}
                    {info.encoding === "ucs2" && <span className="text-muted-foreground/80"> · Unicode</span>}
                  </span>
                  <span className="text-muted-foreground">{text.length}/1600</span>
                </div>
              );
            })()}
            {isMulti && (
              <div className="flex items-start gap-2 rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-2 text-xs" data-testid="send-sms-multi-warning">
                <Info className="h-3.5 w-3.5 text-[hsl(var(--warning))] shrink-0 mt-0.5" />
                <span>
                  This message will be sent as {info.segments} SMS segments{info.encoding === "ucs2" ? " (Unicode characters use shorter segments)" : ""}. You can still send it — you might just want to shorten it first.
                </span>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={sending}
              data-testid="send-sms-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={disabled || !to.trim() || !text.trim()}
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
              data-testid="send-sms-confirm-btn"
            >
              {sending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                : isRetry
                  ? <><RefreshCw className="mr-2 h-4 w-4" /> Retry send</>
                  : <><Send className="mr-2 h-4 w-4" /> Send SMS</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
